import type { FullPokemonCard } from '@/types/pokemon'
import { CardDetails } from './CardDetails'
import { CardmarketLivePrices } from './CardmarketLivePrices'
import { X } from 'lucide-react'

interface PokemonCardModalProps {
  card: FullPokemonCard | null
  isOpen: boolean
  onClose: () => void
}

export function PokemonCardModal({ card, isOpen, onClose }: PokemonCardModalProps) {
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto" onClick={handleBackdropClick}>
      <div className="bg-white rounded-xl shadow-xl border border-gray-200 w-full max-w-2xl max-h-[90vh] overflow-y-auto my-8" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between z-10">
          <h2 className="text-lg font-semibold text-gray-900 m-0 truncate pr-4">{card?.name ?? 'Card details'}</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 hover:text-gray-900 transition-colors shrink-0" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {card ? (
            <>
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="shrink-0 mx-auto sm:mx-0">
                  {card.cardData.imageUrl ? (
                    <img src={card.cardData.imageUrl} alt={card.name} className="w-full max-w-[280px] aspect-63/88 object-contain rounded-lg border border-gray-200 shadow-sm" />
                  ) : (
                    <div className="w-[200px] aspect-63/88 rounded-lg bg-muted border border-gray-200 flex items-center justify-center">
                      <span className="text-muted-foreground text-sm">No image</span>
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-xl font-semibold text-gray-900 mb-1">{card.name}</h3>
                  {card.set && <p className="text-muted-foreground text-sm mb-2">{card.set.name}</p>}
                  {card.cardMarketUrl && (
                    <a href={card.cardMarketUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 hover:underline inline-block">
                      View on Cardmarket →
                    </a>
                  )}
                </div>
              </div>

              <CardmarketLivePrices card={card} />
              <CardDetails card={card} />
            </>
          ) : (
            <p className="text-muted-foreground text-center py-8">No card selected.</p>
          )}
        </div>
      </div>
    </div>
  )
}
